import axios from "axios";
import { publicUrl, routerPath } from "utils/publicUrl";

const SSO_BASE_URL = (
  process.env.REACT_APP_USER_SERVICE || process.env.REACT_APP_API_URL
)?.replace(/\/api\/v1\/?$/, "");

/**
 * SSO exchange request
 */
interface SSOExchangeRequest {
  code: string;
}

/**
 * SSO exchange response from backend - now matches regular login response format
 */
interface SSOExchangeResponse {
  id: string;
  firstName: string;
  email: string;
  lastName: string;
  username: string;
  jwtToken: string;
  status: string;
  jobTitle: string;
  profilePictureImageSrc: string;
  errorMessage: string;
  organization: {
    createdDate: string;
    updateDate: string | null;
    country: string;
    id: string;
    name: string;
    emailDomain: string;
    requireMfaForOrganization: boolean;
    allowUserDefinedFields: boolean;
    organizationAccountType: string;
    accountExpiryDate: string | null;
    address: string;
    provinceState: string;
    postalCode: string;
    phoneNumber: string;
    city: string;
    modulesSubscriptionGroups: Record<string, unknown>;
    primaryContact: {
      firstName: string;
      lastName: string;
      phoneNumber: string;
    };
    maximumUsers: number;
    hasUnlimitedUsers: boolean;
    betaFeatures: string[];
    authenticationMethods: string[];
  };
  role: number;
  dataAccess: [];
  dataSources: [];
  canImpersonate: boolean;
  isImpersonating: boolean;
  impersonator: string;
  impersonateRequestId: string;
  phoneNumber: string;
  sessionBroadcastToken: string;
  preferredMFAMethod: number;
}

/**
 * Initiates the SSO login process by redirecting to the backend SSO endpoint
 * @param organizationId - The organization ID for SSO
 * @param returnUrl - Optional URL to return to after authentication (where user goes after SSO completes)
 */
export const initiateSSO = (organizationId: string, returnUrl: string | null = null) => {
  // The callback URL is always our SSO callback endpoint
  const callbackUrl = `${window.location.origin}${publicUrl("auth/sso/callback")}`;

  // A router path, not the browser pathname: the callback pushes it through the
  // router, which adds the base itself.
  const finalReturnUrl = returnUrl || routerPath(window.location.pathname);

  const params = new URLSearchParams({
    organizationId,
    callbackUrl, // Where IdP should redirect back to
    returnUrl: finalReturnUrl // Where user should go after successful processing
  });

  // Redirect to SSO initiation endpoint
  window.location.href = `${SSO_BASE_URL}/sso/initiate?${params.toString()}`;
};

/**
 * Checks if SSO is enabled for a given email domain
 * @param email - The user's email address
 * @returns Promise resolving to organization ID if SSO is enabled, null otherwise
 */
export const checkSSOByEmail = async (email: string): Promise<string | null> => {
  try {
    const domain = email.split("@")[1];
    if (!domain) {
      return null;
    }

    const response = await axios.get(`${SSO_BASE_URL}/api/v1/sso/check?domain=${domain}`);

    // Backend returns { isEnabled: boolean, organizationId: string, ... }
    if (response.data.isEnabled && response.data.organizationId) {
      return response.data.organizationId;
    }

    return null;
  } catch {
    return null;
  }
};

/**
 * Checks if SSO is enabled for a given organization
 * @param organizationId - The organization ID to check
 * @returns Promise resolving to true if SSO is enabled
 */
export const checkSSOEnabled = async (organizationId: string): Promise<boolean> => {
  try {
    const response = await axios.get(
      `${SSO_BASE_URL}/api/v1/sso/metadata?organizationId=${organizationId}`
    );
    return response.status === 200;
  } catch {
    return false;
  }
};

/**
 * Exchanges SSO session code for JWT tokens
 * @param code - Session code from SSO callback
 * @returns Promise resolving to SSO tokens and user info
 */
export const exchangeSSO = async (code: string): Promise<SSOExchangeResponse> => {
  const request: SSOExchangeRequest = { code };

  try {
    const response = await axios.post<SSOExchangeResponse>(
      `${SSO_BASE_URL}/api/v1/sso/exchange`,
      request,
      {
        headers: {
          "Content-Type": "application/json"
        },
        withCredentials: true
      }
    );

    return response.data;
  } catch (error) {
    throw new Error("Failed to exchange SSO code for tokens");
  }
};
